import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button, Alert, Card, Form } from 'react-bootstrap';
import authService from '../services/authService';
import businessService from '../services/businessService';
import BackToDashboard from '../components/BackToDashboard';

function ReferralSource() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const [referralPercentage, setReferralPercentage] = useState('');
  const [existingData, setExistingData] = useState(false);

  useEffect(() => {
    checkAuthAndLoad();
  }, []);

  const checkAuthAndLoad = async () => {
    const authResult = await authService.checkAuth();
    if (!authResult.authenticated) {
      navigate('/login');
      return;
    }

    // Check for existing data
    const result = await businessService.getField('referral_percentage');
    if (result.success && result.data.field_value) {
      setReferralPercentage(result.data.field_value);
      setExistingData(true);
    }

    setLoading(false);
  };

  const handleSave = async () => {
    if (!referralPercentage) {
      setError('Please select an option');
      return;
    }

    setSaving(true);
    setError('');

    const result = await businessService.saveField('referral_percentage', referralPercentage);

    if (result.success) {
      navigate('/customer-experience');
    } else {
      setError(result.error || 'Failed to save');
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <p>Loading...</p>
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <BackToDashboard module="business-model" />
      <Row className="justify-content-center">
        <Col md={8} lg={6}>
          <Card className="shadow">
            <Card.Body className="p-4">
              <h2 className="mb-4">Customer Acquisition Mix</h2>

              <p className="text-muted mb-4">
                This helps us tailor your customer experience and marketing strategy.
              </p>

              {error && <Alert variant="danger">{error}</Alert>}

              <Form>
                <Form.Group className="mb-4">
                  <Form.Label className="fw-bold">
                    What percentage of your new customers come from referrals or word-of-mouth?
                  </Form.Label>

                  <div className="mt-3">
                    <Form.Check
                      type="radio"
                      id="referral-low"
                      name="referralPercentage"
                      label={
                        <span>
                          <strong>Less than 20%</strong>
                          <br />
                          <small className="text-muted">Most customers find you through ads, search, or outreach</small>
                        </span>
                      }
                      value="low"
                      checked={referralPercentage === 'low'}
                      onChange={(e) => setReferralPercentage(e.target.value)}
                      className="mb-3 p-3 border rounded"
                    />

                    <Form.Check
                      type="radio"
                      id="referral-medium"
                      name="referralPercentage"
                      label={
                        <span>
                          <strong>20% - 50%</strong>
                          <br />
                          <small className="text-muted">A healthy mix of referrals and other channels</small>
                        </span>
                      }
                      value="medium"
                      checked={referralPercentage === 'medium'}
                      onChange={(e) => setReferralPercentage(e.target.value)}
                      className="mb-3 p-3 border rounded"
                    />

                    <Form.Check
                      type="radio"
                      id="referral-high"
                      name="referralPercentage"
                      label={
                        <span>
                          <strong>More than 50%</strong>
                          <br />
                          <small className="text-muted">Your existing customers are your main growth engine</small>
                        </span>
                      }
                      value="high"
                      checked={referralPercentage === 'high'}
                      onChange={(e) => setReferralPercentage(e.target.value)}
                      className="mb-3 p-3 border rounded"
                    />

                    <Form.Check
                      type="radio"
                      id="referral-unknown"
                      name="referralPercentage"
                      label={
                        <span>
                          <strong>I don't know</strong>
                          <br />
                          <small className="text-muted">We'll help you start tracking this</small>
                        </span>
                      }
                      value="unknown"
                      checked={referralPercentage === 'unknown'}
                      onChange={(e) => setReferralPercentage(e.target.value)}
                      className="mb-3 p-3 border rounded"
                    />
                  </div>
                </Form.Group>

                {referralPercentage === 'high' && (
                  <Alert variant="info" className="mb-4">
                    <strong>Great!</strong> With strong referral-based growth, we'll focus your customer experience
                    on maximizing referrals, capturing testimonials at peak satisfaction, and making it easy
                    for happy customers to spread the word.
                  </Alert>
                )}

                {referralPercentage === 'low' && (
                  <Alert variant="info" className="mb-4">
                    <strong>Noted.</strong> We'll focus your customer experience on building proof
                    (reviews, testimonials, case studies) and creating moments that encourage customers
                    to share their experience.
                  </Alert>
                )}

                {referralPercentage === 'unknown' && (
                  <Alert variant="warning" className="mb-4">
                    <strong>First priority:</strong> Start asking every new customer "How did you hear about us?"
                    This one question will unlock huge insights about where to focus your marketing.
                  </Alert>
                )}

                <div className="d-flex justify-content-between mt-4">
                  <Button
                    variant="outline-secondary"
                    onClick={() => navigate('/dashboard/business-model')}
                  >
                    ← Back
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleSave}
                    disabled={saving || !referralPercentage}
                  >
                    {saving ? 'Saving...' : 'Continue →'}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}

export default ReferralSource;
